import * as R from 'ramda'
import { RSAA } from 'redux-api-middleware'
import { getApiPath, makeQueryParams } from 'sidecar-utility-library'

import { initialState, types } from './types'

const SESSION_REFRESH_MESSAGE =
  'Your session needs refreshing. Please sign out, sign back in, and try again. If this keeps happening, contact Member Care.'

// member-app's route changes the password in WorkOS, when the member has a
// WorkOS user, and in the account service.
export const updatePassword = ({ currentPassword, newPassword }) => ({
  [RSAA]: {
    endpoint: '/api/auth/password',
    method: 'PUT',
    body: JSON.stringify({
      encodedPassword: btoa(currentPassword),
      encodedNewPassword: btoa(newPassword),
    }),
    types: [
      types.UPDATE_PASSWORD_REQUEST,
      types.UPDATE_PASSWORD_SUCCESS,
      types.UPDATE_PASSWORD_ERROR,
    ],
  },
})

export const requestPasswordReset = email => ({
  [RSAA]: {
    endpoint: getApiPath(`auth`, `password/reset/request`),
    method: 'PUT',
    headers: {
      token: 'IGNORE',
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    body: JSON.stringify({ email }),
    types: [
      types.FORGOT_PASSWORD_REQUEST,
      types.FORGOT_PASSWORD_SUCCESS,
      types.FORGOT_PASSWORD_ERROR,
    ],
  },
})

// Member-app's reset route checks the token and resets the password in WorkOS
// and the auth service, so the member's WorkOS user id stays server-side.
const RESET_PASSWORD_URL = '/api/auth/password/reset'

export const resetPassword = (password, token) => ({
  [RSAA]: {
    endpoint: `${RESET_PASSWORD_URL}${makeQueryParams({
      passwordResetToken: token,
    })}`,
    method: 'PUT',
    headers: { token: 'IGNORE' },
    body: JSON.stringify({ encodedPassword: btoa(password) }),
    types: [
      types.RESET_PASSWORD_REQUEST,
      types.RESET_PASSWORD_SUCCESS,
      types.RESET_PASSWORD_ERROR,
    ],
  },
})

export const checkResetPasswordTokenValidity = passwordResetToken => ({
  [RSAA]: {
    endpoint: `${RESET_PASSWORD_URL}${makeQueryParams({
      passwordResetToken,
    })}`,
    method: 'GET',
    types: [
      types.FETCH_TOKEN_STATUS_REQUEST,
      types.FETCH_TOKEN_STATUS_SUCCESS,
      types.FETCH_TOKEN_STATUS_ERROR,
    ],
  },
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.UPDATE_PASSWORD_REQUEST: {
      return {
        ...state,
        updatePassword: {
          ...state.updatePassword,
          busy: true,
          error: '',
        },
      }
    }
    case types.UPDATE_PASSWORD_SUCCESS: {
      return {
        ...state,
        updatePassword: {
          ...state.updatePassword,
          busy: false,
          error: '',
        },
      }
    }
    case types.UPDATE_PASSWORD_ERROR: {
      return {
        ...state,
        updatePassword: {
          ...state.updatePassword,
          busy: false,
          error:
            payload.status === 401
              ? SESSION_REFRESH_MESSAGE
              : R.pathOr(
                  'Something went wrong',
                  ['response', 'message'],
                  payload,
                ),
        },
      }
    }

    // ----------

    case types.FORGOT_PASSWORD_REQUEST: {
      return {
        ...state,
        forgotPassword: {
          ...state.forgotPassword,
          busy: true,
          error: '',
        },
      }
    }
    case types.FORGOT_PASSWORD_SUCCESS: {
      return {
        ...state,
        forgotPassword: {
          ...state.forgotPassword,
          busy: false,
        },
      }
    }
    case types.FORGOT_PASSWORD_ERROR: {
      return {
        ...state,
        forgotPassword: {
          ...state.forgotPassword,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    // ----------

    case types.RESET_PASSWORD_REQUEST: {
      return {
        ...state,
        resetPassword: {
          ...state.resetPassword,
          busy: true,
          error: '',
        },
      }
    }
    case types.RESET_PASSWORD_SUCCESS: {
      return {
        ...state,
        resetPassword: {
          ...state.resetPassword,
          busy: false,
          message: payload.message,
        },
      }
    }
    case types.RESET_PASSWORD_ERROR: {
      return {
        ...state,
        resetPassword: {
          ...state.resetPassword,
          busy: false,
          error: payload.response?.message,
          errorField: payload.response?.field,
        },
      }
    }

    // ----------

    case types.FETCH_TOKEN_STATUS_REQUEST: {
      return {
        ...state,
        tokenStatus: {
          ...state.tokenStatus,
          busy: true,
        },
      }
    }
    case types.FETCH_TOKEN_STATUS_SUCCESS: {
      return {
        ...state,
        tokenStatus: {
          ...state.tokenStatus,
          busy: false,
          status: type.substring(type.length - 7),
        },
      }
    }
    case types.FETCH_TOKEN_STATUS_ERROR: {
      return {
        ...state,
        tokenStatus: {
          ...state.tokenStatus,
          busy: false,
          error:
            'Your reset token has expired. Follow the instructions below to reset your password.',
        },
      }
    }

    // ----------

    default:
      return state
  }
}
